import { onMounted, ref, type Ref } from 'vue';
import type { NovuSubscriber } from '@monizze/monizze-components/notification-center';
import { fetchNovuSubscriber, getNovuConfig, type NovuConfig } from '@/composables/useNovuClient';

interface UseNotificationCenterReturn {
	/** Connection settings, or `null` when Novu is not configured (feature off). */
	config: NovuConfig | null;
	/** The authenticated subscriber, populated once fetched. Empty until then. */
	subscribers: Ref<NovuSubscriber[]>;
	/**
	 * Organization context for the `organization-id` prop, populated once fetched.
	 * Must match the org the subscriber's `contextHash` was signed for, so it
	 * comes from the same backend response (never derived client-side).
	 */
	organizationId: Ref<string | undefined>;
}

/**
 * Supplies the props the in-app `MzNotificationCenter` needs: the static Novu
 * config and the current user's subscriber identity (fetched from the backend).
 * The component builds its own Novu clients from these, separate from the
 * `initNovuClient` instance used by the notification pages.
 */
export function useNotificationCenter(): UseNotificationCenterReturn {
	const config = getNovuConfig();
	const subscribers: Ref<NovuSubscriber[]> = ref([]);
	const organizationId: Ref<string | undefined> = ref(undefined);

	onMounted(async () => {
		if (!config) return;
		try {
			const subscriber = await fetchNovuSubscriber();
			subscribers.value = [
				{
					subscriberId: subscriber.subscriberId,
					...(subscriber.subscriberHash ? { subscriberHash: subscriber.subscriberHash } : {}),
					...(subscriber.contextHash ? { contextHash: subscriber.contextHash } : {}),
				},
			];
			organizationId.value = subscriber.organization ?? undefined;
		} catch (err) {
			console.error('Failed to load Novu subscriber for the notification center:', err);
		}
	});

	return { config, subscribers, organizationId };
}
